import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import {
  MessageCircle,
  Mail,
  Phone,
  Database,
  CalendarCheck,
  TrendingUp,
  Users,
  Zap,
  Bot,
  BarChart3,
  CheckCircle2,
  Clock,
  ArrowUpRight,
} from "lucide-react";

const METRICS = [
  { label: "Active workflows", value: "12", change: "+3", icon: Zap },
  { label: "Leads today", value: "48", change: "+12%", icon: Users },
  { label: "Response rate", value: "94%", change: "+5%", icon: TrendingUp },
  { label: "Bookings", value: "8", change: "+2", icon: CalendarCheck },
];

const FLOW = [
  { icon: Users, label: "Lead" },
  { icon: Bot, label: "AI" },
  { icon: MessageCircle, label: "WhatsApp" },
  { icon: Mail, label: "Email" },
  { icon: Phone, label: "Voice" },
  { icon: Database, label: "CRM" },
  { icon: CalendarCheck, label: "Booked" },
];

const ACTIVITY = [
  { text: "New lead from WhatsApp", time: "2m ago" },
  { text: "AI replied to email inquiry", time: "5m ago" },
  { text: "Appointment booked automatically", time: "12m ago" },
  { text: "CRM updated for Rahul Sharma", time: "18m ago" },
  { text: "Voice call completed", time: "24m ago" },
];

export default function HeroVisual() {
  const containerRef = useRef(null);
  const dashboardRef = useRef(null);
  const floatersRef = useRef([]);

  useEffect(() => {
    if (!containerRef.current || !dashboardRef.current) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      gsap.set([dashboardRef.current, ...floatersRef.current.filter(Boolean)], { opacity: 1, y: 0, scale: 1 });
      return;
    }

    const tl = gsap.timeline({ delay: 1.1 });

    tl.fromTo(
      dashboardRef.current,
      { opacity: 0, y: 50, scale: 0.96, rotateX: 8 },
      { opacity: 1, y: 0, scale: 1, rotateX: 0, duration: 1.1, ease: "power3.out" }
    );

    floatersRef.current.forEach((el, i) => {
      if (!el) return;
      tl.fromTo(
        el,
        { opacity: 0, y: 30, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: "back.out(1.4)" },
        1.3 + i * 0.12
      );
    });

    return () => tl.kill();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;

      floatersRef.current.forEach((el, i) => {
        if (!el) return;
        const depth = (i % 3) + 1;
        gsap.to(el, {
          x: x * 18 * depth,
          y: y * 14 * depth,
          duration: 0.8,
          ease: "power2.out",
        });
      });

      gsap.to(dashboardRef.current, {
        rotateY: x * 2,
        rotateX: -y * 2,
        duration: 0.8,
        ease: "power2.out",
      });
    };

    const onLeave = () => {
      floatersRef.current.forEach((el) => {
        if (!el) return;
        gsap.to(el, { x: 0, y: 0, duration: 0.8, ease: "power2.out" });
      });
      gsap.to(dashboardRef.current, { rotateY: 0, rotateX: 0, duration: 0.8, ease: "power2.out" });
    };

    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);

    return () => {
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative mt-16 md:mt-20 mx-auto max-w-5xl" style={{ perspective: "1200px" }}>
      <div
        ref={dashboardRef}
        className="relative glass-strong rounded-[2rem] border border-[var(--glass-border)] shadow-2xl shadow-orange-500/5 overflow-hidden"
        style={{ opacity: 0, transformStyle: "preserve-3d" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--hairline)]">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-orange-500" />
              <span className="h-3 w-3 rounded-full bg-amber-400" />
              <span className="h-3 w-3 rounded-full bg-green-400" />
            </div>
            <span className="font-semibold text-[var(--body-fg)] ml-2">AI Automation Dashboard</span>
          </div>
          <span className="text-xs font-mono text-[var(--muted-text)] uppercase tracking-wider flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Live
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[var(--hairline)]">
          {/* Metrics column */}
          <div className="md:col-span-2 bg-[var(--body-bg)] p-6">
            <h4 className="text-sm font-medium text-[var(--muted-text)] mb-4">Today&apos;s performance</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {METRICS.map((m, i) => {
                const Icon = m.icon;
                return (
                  <div key={i} className="bg-white/60 rounded-2xl p-4 border border-[var(--glass-border)] hover:border-[var(--card-hover-border)] transition-colors">
                    <Icon className="w-4 h-4 text-[var(--brand)] mb-2" />
                    <div className="text-2xl font-semibold text-[var(--body-fg)]">{m.value}</div>
                    <div className="text-xs text-[var(--muted-text)]">{m.label}</div>
                    <div className="text-xs text-green-600 mt-1 font-medium">{m.change}</div>
                  </div>
                );
              })}
            </div>

            {/* Mini workflow */}
            <div className="mt-6 bg-white/60 rounded-2xl p-5 border border-[var(--glass-border)]">
              <h4 className="text-sm font-medium text-[var(--muted-text)] mb-4">Active workflow</h4>
              <div className="flex flex-wrap items-center gap-2">
                {FLOW.map((step, i) => {
                  const Icon = step.icon;
                  const isLast = i === FLOW.length - 1;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex flex-col items-center gap-1">
                        <span className="h-9 w-9 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[var(--brand)] hover:bg-orange-100 transition-colors">
                          <Icon className="w-4 h-4" />
                        </span>
                        <span className="text-[10px] text-[var(--muted-text)]">{step.label}</span>
                      </div>
                      {!isLast && (
                        <span className="text-[var(--brand)]/40 text-lg">→</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Activity feed */}
          <div className="bg-[var(--body-bg)] p-6">
            <h4 className="text-sm font-medium text-[var(--muted-text)] mb-4">Recent activity</h4>
            <div className="space-y-4">
              {ACTIVITY.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--brand)]" />
                  <div>
                    <p className="text-sm text-[var(--body-fg)]">{item.text}</p>
                    <p className="text-xs text-[var(--muted-text)]">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp card */}
      <div
        ref={(el) => (floatersRef.current[0] = el)}
        className="absolute -left-4 md:-left-12 top-1/4 glass-strong rounded-2xl p-4 border border-[var(--glass-border)] shadow-xl shadow-orange-500/5 max-w-[200px]"
        style={{ opacity: 0 }}
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center text-green-600">
            <MessageCircle className="w-4 h-4" />
          </span>
          <span className="text-xs font-medium text-[var(--body-fg)]">WhatsApp AI</span>
        </div>
        <div className="space-y-2">
          <div className="bg-orange-500/10 rounded-lg rounded-tl-none p-2.5 text-xs text-[var(--body-fg)]">
            Hi! I can book your appointment instantly.
          </div>
          <div className="bg-[var(--sand)] rounded-lg rounded-tr-none p-2.5 text-xs text-[var(--body-fg)] ml-4">
            Tomorrow 3 PM works.
          </div>
        </div>
      </div>

      {/* Floating analytics card */}
      <div
        ref={(el) => (floatersRef.current[1] = el)}
        className="absolute -right-4 md:-right-10 top-1/3 glass-strong rounded-2xl p-4 border border-[var(--glass-border)] shadow-xl shadow-orange-500/5"
        style={{ opacity: 0 }}
      >
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-[var(--brand)]" />
          <span className="text-xs font-medium text-[var(--body-fg)]">Conversions</span>
        </div>
        <div className="text-2xl font-semibold text-[var(--body-fg)]">+42%</div>
        <div className="flex items-end gap-1 mt-2 h-8">
          {[40, 60, 45, 80, 65, 90, 75].map((h, i) => (
            <div
              key={i}
              className="w-2.5 rounded-t bg-gradient-to-t from-orange-500 to-orange-300"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      </div>

      {/* Floating voice card */}
      <div
        ref={(el) => (floatersRef.current[2] = el)}
        className="absolute left-1/4 -bottom-6 md:-bottom-10 glass-strong rounded-2xl p-3 border border-[var(--glass-border)] shadow-xl shadow-orange-500/5 flex items-center gap-3"
        style={{ opacity: 0 }}
      >
        <span className="w-10 h-10 rounded-full bg-mint flex items-center justify-center text-green-600">
          <Phone className="w-4 h-4" />
        </span>
        <div>
          <div className="text-xs font-medium text-[var(--body-fg)]">Voice AI on call</div>
          <div className="flex items-center gap-0.5 mt-1 h-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="w-0.5 rounded-full bg-green-500"
                style={{
                  height: `${30 + Math.random() * 70}%`,
                  animation: `wave-bar ${0.8 + Math.random() * 0.6}s ease-in-out infinite`,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Floating booked card */}
      <div
        ref={(el) => (floatersRef.current[3] = el)}
        className="absolute right-1/4 -bottom-4 md:-bottom-8 glass-strong rounded-2xl p-3 border border-[var(--glass-border)] shadow-xl shadow-orange-500/5 flex items-center gap-3"
        style={{ opacity: 0 }}
      >
        <span className="w-10 h-10 rounded-full bg-sky flex items-center justify-center text-sky-600">
          <CheckCircle2 className="w-5 h-5" />
        </span>
        <div>
          <div className="text-xs font-medium text-[var(--body-fg)]">Appointment booked</div>
          <div className="text-[10px] text-[var(--muted-text)] flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" /> Today, 4:30 PM
          </div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-[var(--brand)]" />
      </div>
    </div>
  );
}
